import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Languages, Image, LayoutDashboard, Link2, FileEdit, LogOut, Users } from 'lucide-react';
import Dashboard from './pages/Dashboard';
import Translations from './pages/Translations';
import Images from './pages/Images';
import Settings from './pages/Settings';
import PageEditor from './pages/PageEditor';
import CustomerTranslations from './pages/CustomerTranslations';
import Login from './pages/Login';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    const email = localStorage.getItem('userEmail');
    if (authStatus === 'true' && email) {
      setIsAuthenticated(true);
      setUserEmail(email);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setUserEmail(localStorage.getItem('userEmail') || '');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    setIsAuthenticated(false);
    setUserEmail('');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userEmail) return 'U';
    const parts = userEmail.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return userEmail[0].toUpperCase();
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="app-container">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <h1>Avopla</h1>
            <span>Translation Admin</span>
          </div>

          <nav className="sidebar-nav">
            <div className="nav-section">
              <div className="nav-section-title">Overview</div>
              <NavLink
                to="/"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                end
              >
                <LayoutDashboard />
                Dashboard
              </NavLink>
            </div>

            <div className="nav-section">
              <div className="nav-section-title">Content</div>
              <NavLink
                to="/pages"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <FileEdit />
                Page Editor
              </NavLink>
              <NavLink
                to="/translations"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Languages />
                All Translations
              </NavLink>
              <NavLink
                to="/images"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Image />
                Images
              </NavLink>
            </div>

            <div className="nav-section">
              <div className="nav-section-title">Customer Website</div>
              <NavLink
                to="/customer-translations"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Users />
                Customer Side
              </NavLink>
            </div>

            <div className="nav-section">
              <div className="nav-section-title">Settings</div>
              <NavLink
                to="/settings"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Link2 />
                Links & URLs
              </NavLink>
            </div>
          </nav>

          {/* User Menu */}
          <div className="user-menu">
            <div className="user-avatar">
              {getUserInitials()}
            </div>
            <div className="user-info">
              <div className="user-name">Admin User</div>
              <div className="user-email">{userEmail}</div>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={18} />
            </button>
          </div>

          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--border)',
            fontSize: '11px',
            color: 'var(--text-tertiary)'
          }}>
            © 2025 Avopla Inc.
          </div>
        </aside>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pages" element={<PageEditor />} />
            <Route path="/translations" element={<Translations />} />
            <Route path="/images" element={<Images />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/customer-translations" element={<CustomerTranslations />} />
          </Routes>
        </main>

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </Router>
  );
}

export default App;
